import React, {createContext, PropsWithChildren, useContext, useEffect} from 'react';
import {Account, JWTDecoded, UserAccess} from "./types";
import {create} from 'zustand'
import {createJSONStorage, persist} from 'zustand/middleware'
import {message} from "antd";

type LauncherAuthState = {
    user: Account | undefined;
    expires: number | undefined;
    access: UserAccess | undefined;
};

const defaultState: LauncherAuthState = {
    user: undefined,
    expires: undefined,
    access: undefined,
};

type LauncherAuthApi = LauncherAuthState & {
    /**
     * Updates the stored user account information without changing the session expiration.
     * Useful when user details change during an active session.
     */
    updateAccount: (user: Account) => void;
    /**
     * Establishes a user session with the provided account details and JWT token.
     * Sets the user state and expiration time based on the JWT.
     */
    authenticateUser: (user: Account, jwt: JWTDecoded) => void;
    /**
     * Clears the current user session and resets the state to default.
     */
    logoutUser: () => void;
    /**
     * Function.
     *
     * Returns true when called and the user currently has an active session.
     */
    isUserLoggedIn: () => boolean;
    /**
     * Function.
     * 
     * Returns true when called and the user is verified.
     */
    isUserVerified: () => boolean;
    /**
     * Function.
     *
     * Returns true if the current user has a profile associated with their account.
     */
    hasProfile: () => boolean;
    /**
     * Function.
     *
     * Returns true if the current user has narrator privileges.
     */
    isNarrator: () => boolean;
    /**
     * Function.
     *
     * Returns true if the current user has administrator privileges.
     */
    isAdmin: () => boolean;
    /**
     * Function.
     *
     * Returns the access of the user
     */
    getUserAccess: () => UserAccess | undefined;
    /**
     * Function.
     *
     * Updates the access list for the current user
     */
    setUserAccess: (access: UserAccess) => void;
}

const AuthStateContext = createContext<LauncherAuthApi | undefined>(undefined);

export const useAuthStoreApi = create<LauncherAuthApi>()(
    persist(
        (set, get) => ({
            ...defaultState,
            authenticateUser: (user: Account, jwt: JWTDecoded) => {
                set({
                    user,
                    expires: (jwt.exp || 0) * 1000,
                });
            },
            updateAccount: (user: Account) => {
                const expires = get().expires || 0;
                set(defaultState);
                set({
                    user,
                    expires
                });
            },
            logoutUser: () => {
                set(defaultState);
            },
            isUserLoggedIn: () => !!get().user,
            isAdmin: () => {
                return get().user?.admin || false;
            },
            isNarrator: () => !!get().user?.narrator,
            isUserVerified: () => get().user?.status === "VERIFIED",
            hasProfile: () => !!get().user?.profile,
            setUserAccess: (access: UserAccess) => {
                set({ access });
            },
            getUserAccess: () => {
                return get().access;
            }
        }),
        {
            name: 'rpg-lodge-launcher-auth-user', // name of the item in the storage (must be unique)
            storage: createJSONStorage(() => localStorage), // (optional) by default, 'localStorage' is used,
            onRehydrateStorage: (api) => {
                if (api.expires && api.expires < new Date().getTime()) {
                    api.logoutUser();
                }
            }
        },
    ),
)

export  const AuthStateContextProvider: React.FC<PropsWithChildren> = ({children}) => {
    const api = useAuthStoreApi();
    const { expires, logoutUser } = api;

    useEffect(() => {
        if (!!expires && expires < new Date().getTime()) {
            logoutUser();
            message.warning('Your session expired.');
        }
    }, [expires]);

    return (
        <AuthStateContext.Provider value={api}>
            {children}
        </AuthStateContext.Provider>
    )
};

export const useAuthContext = () => {
    const context = useContext(AuthStateContext);
    if (!context) {
        throw new Error("You are depending on useAuthContext() without placing the component inside an AuthContextProvider.")
    }

    return context;
}
